'use client'

import { useCallback, useState, useSyncExternalStore } from 'react'

/**
 * The sidebar's collapsed state — the stored preference and the viewport rule, in one place.
 *
 * `collapsed` is what the sidebar renders as, not what the user chose. Below the `lg` breakpoint
 * the sidebar is collapsed whatever is stored, and the preference is left alone so it comes back
 * when the viewport widens. The media query arrived here from `app/app/layout-client.tsx:32-43`
 * on #316: it is viewport state rather than keyboard state, and the route tier was recomputing
 * `!isDesktop || collapsed` where no test could see it.
 *
 * `setCollapsed` is gone with the move. It had no caller (measured on the graph at `3aa3c8a`:
 * `layout-client.tsx` destructured `collapsed` and `toggle` and nothing else), and a face is an
 * interface rather than a bag.
 */

const STORAGE_KEY = 'konstantout-sidebar-collapsed'

// The `lg` breakpoint. Tailwind's own value, repeated here because a media query string is not
// something the class names can hand back.
const LG_QUERY = '(min-width: 1024px)'

function subscribeToLg(onChange: () => void) {
  const mql = window.matchMedia(LG_QUERY)
  mql.addEventListener('change', onChange)
  return () => mql.removeEventListener('change', onChange)
}

function getIsDesktop() {
  return typeof window !== 'undefined' && window.matchMedia(LG_QUERY).matches
}

// The server renders the mobile branch, so it never claims desktop.
function getIsDesktopServer() {
  return false
}

function readPreference(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

function writePreference(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(value))
  } catch {
    // localStorage unavailable — the preference is a convenience, not state to recover.
  }
}

export interface SidebarState {
  /** What the sidebar renders as: the stored preference, or forced true below `lg`. */
  collapsed: boolean
  toggle: () => void
}

export function useSidebarState(): SidebarState {
  const [preferCollapsed, setPreferCollapsed] =
    useState<boolean>(readPreference)
  const isDesktop = useSyncExternalStore(
    subscribeToLg,
    getIsDesktop,
    getIsDesktopServer,
  )

  const toggle = useCallback(() => {
    setPreferCollapsed((prev) => {
      const next = !prev
      writePreference(next)
      return next
    })
  }, [])

  return { collapsed: !isDesktop || preferCollapsed, toggle }
}
